import { getAuthHeaders } from '../config/api';
import { apiClient } from './apiClient';

export interface Post {
  id: number;
  title: string;
  slug: string;
  metaDescription?: string;
  primaryKeyword?: string;
  secondaryKeywords?: string[];
  anchorText?: string;
  content: string;
  featuredImage?: string;
  categoryId?: number;
  category?: {
    id: number;
    name: string;
    slug: string;
  };
  tags?: string[];
  internalLinks?: string[];
  externalLinks?: string[];
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason?: string;
  authorId: number;
  author?: {
    id: number;
    fullName?: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
  approvedAt?: string;
  approvedBy?: number;
  approver?: {
    id: number;
    fullName?: string;
    email: string;
  };
  viewCount: number;
  shareCount?: number;
}

export interface PostsResponse {
  posts: Post[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface AdminStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  deleted: number;
}

export interface ApprovePostRequest {
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason?: string;
}

class CensorService {
  /**
   * Helper method để thực hiện API calls
   */
  private async apiCall<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const response = await apiClient.request(endpoint, {
      ...options,
      headers: {
        ...getAuthHeaders(),
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Có lỗi xảy ra';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }

    return response.json();
  }


  /**
   * Lấy danh sách bài viết chờ duyệt
   */
  async getPendingPosts(page: number = 1, limit: number = 10): Promise<PostsResponse> {
    return this.apiCall<PostsResponse>(`/posts/admin/pending?page=${page}&limit=${limit}`);
  }

  /**
   * Lấy danh sách bài viết đã được duyệt
   */
  async getApprovedPosts(page: number = 1, limit: number = 10): Promise<PostsResponse> {
    return this.apiCall<PostsResponse>(`/posts/admin/approved?page=${page}&limit=${limit}`);
  }

  /**
   * Lấy danh sách bài viết bị từ chối
   */
  async getRejectedPosts(page: number = 1, limit: number = 10): Promise<PostsResponse> {
    return this.apiCall<PostsResponse>(`/posts/admin/rejected?page=${page}&limit=${limit}`);
  }

  /**
   * Lấy thống kê tổng quan cho admin
   */
  async getAdminStats(): Promise<AdminStats> {
    return this.apiCall<AdminStats>('/posts/admin/stats');
  }

  /**
   * Duyệt hoặc từ chối bài viết
   */
  async approvePost(postId: number, data: ApprovePostRequest): Promise<Post> {
    return this.apiCall<Post>(`/posts/${postId}/approve`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * Lấy chi tiết bài viết
   */
  async getPostById(postId: number): Promise<Post> {
    return this.apiCall<Post>(`/posts/${postId}`);
  }

  /**
   * Duyệt nhiều bài viết cùng lúc
   * @param ids - Mảng ID của các bài viết cần duyệt
   * @returns Promise<{message: string, approvedCount: number}> - Thông báo duyệt thành công và số lượng bài viết đã duyệt
   * @throws Error - Khi duyệt bài viết thất bại
   */
  async bulkApprovePosts(ids: number[]): Promise<{message: string, approvedCount: number}> {
    if (!ids || ids.length === 0) {
      throw new Error('Không có bài viết nào để duyệt');
    }

    const results = [];
    const errors = [];

    // Duyệt từng bài viết một cách tuần tự để tránh lỗi
    for (const id of ids) {
      try {
        await this.approvePost(id, { status: "approved" });
        results.push(id);
      } catch (error) {
        errors.push({ id, error: error instanceof Error ? error.message : 'Lỗi không xác định' });
      }
    }

    if (errors.length > 0) {
      const errorMessage = errors.length === ids.length 
        ? 'Không thể duyệt bài viết nào'
        : `Duyệt thành công ${results.length}/${ids.length} bài viết. Lỗi: ${errors.map(e => `ID ${e.id}: ${e.error}`).join(', ')}`;
      throw new Error(errorMessage);
    }

    return {
      message: `Đã duyệt thành công ${results.length} bài viết`,
      approvedCount: results.length
    };
  }

  /**
   * Từ chối nhiều bài viết cùng lúc
   * @param ids - Mảng ID của các bài viết cần từ chối
   * @param rejectionReason - Lý do từ chối
   * @returns Promise<{message: string, rejectedCount: number}> - Thông báo từ chối thành công và số lượng bài viết đã từ chối
   * @throws Error - Khi từ chối bài viết thất bại
   */
  async bulkRejectPosts(ids: number[], rejectionReason: string): Promise<{message: string, rejectedCount: number}> {
    if (!ids || ids.length === 0) {
      throw new Error('Không có bài viết nào để từ chối');
    }

    if (!rejectionReason.trim()) {
      throw new Error('Vui lòng nhập lý do từ chối');
    }

    const results = [];
    const errors = [];

    // Từ chối từng bài viết một cách tuần tự để tránh lỗi
    for (const id of ids) {
      try {
        await this.approvePost(id, { status: "rejected", rejectionReason: rejectionReason.trim() });
        results.push(id);
      } catch (error) {
        errors.push({ id, error: error instanceof Error ? error.message : 'Lỗi không xác định' });
      }
    }

    if (errors.length > 0) {
      const errorMessage = errors.length === ids.length 
        ? 'Không thể từ chối bài viết nào'
        : `Từ chối thành công ${results.length}/${ids.length} bài viết. Lỗi: ${errors.map(e => `ID ${e.id}: ${e.error}`).join(', ')}`;
      throw new Error(errorMessage);
    }

    return {
      message: `Đã từ chối thành công ${results.length} bài viết`,
      rejectedCount: results.length
    };
  }
}

export const censorService = new CensorService();
